/**
 * The club's Stripe connection, from the club's side.
 *
 * Written for a volunteer treasurer, so it says what is true and what to do
 * about it rather than showing a status enum. The three unhappy states —
 * never started, started and abandoned, submitted but not yet approved — each
 * need a different sentence and a different button, and collapsing them into
 * "not available" is how a club sits for a fortnight not knowing Stripe is
 * waiting on a bank statement.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Loading, Pill, errorMessage, useToast } from './ui';

interface StripeStatus {
  platformConfigured: boolean;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  canTakeCards: boolean;
  problem: string | null;
  commission: string | null;
}

const money = (cents: number, currency = 'CAD') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(cents / 100);

export function StripeConnectCard() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['billing', 'stripe'],
    queryFn: () => api.get<{ data: StripeStatus }>('/billing/stripe').then((r) => r.data),
  });

  const balance = useQuery({
    queryKey: ['billing', 'balance'],
    queryFn: () => api.get<{
      data: { availableCents: number; pendingCents: number; currency: string } | null;
    }>('/billing/balance').then((r) => r.data),
    enabled: Boolean(status.data?.chargesEnabled),
  });

  // Both links are single use and short lived, so they are fetched on click
  // and followed immediately rather than rendered as an href.
  const onboard = useMutation({
    mutationFn: () => api.post<{ data: { url: string } }>('/billing/stripe/onboard'),
    onSuccess: (r) => { window.location.href = r.data.url; },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const dashboard = useMutation({
    mutationFn: () => api.post<{ data: { url: string } }>('/billing/stripe/dashboard'),
    onSuccess: (r) => { window.open(r.data.url, '_blank', 'noopener'); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (status.isLoading) return <Card title="Online payment"><Loading /></Card>;

  const s = status.data;
  if (!s) return null;

  if (!s.platformConfigured) {
    return (
      <Card title="Online payment">
        <p className="small">
          Card payment is not switched on for this installation. Members can
          still be marked as paid at the club.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="Online payment"
      actions={s.canTakeCards
        ? <Pill tone="good">taking cards</Pill>
        : <Pill tone="warn">not taking cards</Pill>}
    >
      {s.canTakeCards ? (
        <>
          <p>
            Members can pay by card when they register, and the money goes
            straight into the club&rsquo;s own Stripe account.
          </p>
          {balance.data && (
            <p className="small">
              Stripe is holding {money(balance.data.availableCents, balance.data.currency.toUpperCase())} available
              {balance.data.pendingCents > 0
                && `, and ${money(balance.data.pendingCents, balance.data.currency.toUpperCase())} still clearing`}.
            </p>
          )}
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem' }}>
            <button className="btn btn--sm" type="button" disabled={dashboard.isPending}
              onClick={() => dashboard.mutate()}>
              Open our Stripe dashboard
            </button>
            <button className="btn btn--sm" type="button" disabled={onboard.isPending}
              onClick={() => onboard.mutate()}>
              Update our details
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="banner">{s.problem}</div>
          <p className="small">
            Stripe collects the club&rsquo;s bank details and verifies whoever
            signs up. None of that is stored on this system.
          </p>
          <button
            className="btn btn--primary"
            type="button"
            disabled={onboard.isPending}
            onClick={() => onboard.mutate()}
          >
            {s.accountId ? 'Finish connecting Stripe' : 'Connect the club’s Stripe account'}
          </button>
        </>
      )}

      {s.commission && (
        <p className="small" style={{ marginTop: '1rem', color: 'var(--text-soft)' }}>
          {s.commission}
        </p>
      )}

      <button
        className="btn btn--sm"
        type="button"
        style={{ marginTop: '.75rem' }}
        onClick={() => void queryClient.invalidateQueries({ queryKey: ['billing'] })}
      >
        Refresh
      </button>
    </Card>
  );
}
